import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PrescriptionCard from './components/PrescriptionCard';
import PrescriptionForm from './components/PrescriptionForm';
import PrescriptionFilters from './components/PrescriptionFilters';
import PrescriptionAnalytics from './components/PrescriptionAnalytics';
import PrescriptionTemplates from './components/PrescriptionTemplates';

const PrescriptionManagement = () => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [filteredPrescriptions, setFilteredPrescriptions] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPrescription, setEditingPrescription] = useState(null);
  const [activeTab, setActiveTab] = useState('prescriptions');
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('createdDate');
  const [sortOrder, setSortOrder] = useState('desc');

  // Mock prescription data
  const mockPrescriptions = [
    {
      id: 'RX001',
      patientId: 'p001',
      patientName: 'Sarah Johnson',
      medication: 'Lisinopril',
      dosage: '10mg',
      frequency: 'once_daily',
      duration: '30 days',
      instructions: 'Take with or without food. Monitor blood pressure regularly.',
      status: 'active',
      priority: 'normal',
      createdDate: '2025-09-20',
      expiryDate: '2025-10-20',
      refills: 2,
      isBlockchainVerified: true,
      isFHIRCompliant: true,
      pharmacy: {
        id: 'ph001',
        name: 'CVS Pharmacy',
        address: '123 Main St, City, State 12345'
      },
      fulfillmentStatus: 'ready'
    },
    {
      id: 'RX002',
      patientId: 'p002',
      patientName: 'Michael Chen',
      medication: 'Metformin',
      dosage: '500mg',
      frequency: 'twice_daily',
      duration: '30 days',
      instructions: 'Take with meals to reduce stomach upset. Monitor blood glucose.',
      status: 'fulfilled',
      priority: 'normal',
      createdDate: '2025-09-19',
      expiryDate: '2025-10-19',
      refills: 5,
      isBlockchainVerified: true,
      isFHIRCompliant: true,
      pharmacy: {
        id: 'ph002',
        name: 'Walgreens',
        address: '456 Oak Ave, City, State 12345'
      },
      fulfillmentStatus: 'completed'
    },
    {
      id: 'RX003',
      patientId: 'p003',
      patientName: 'Emily Rodriguez',
      medication: 'Atorvastatin',
      dosage: '20mg',
      frequency: 'once_daily',
      duration: '30 days',
      instructions: 'Take in the evening. Avoid grapefruit juice.',
      status: 'pending',
      priority: 'high',
      createdDate: '2025-09-22',
      expiryDate: '2025-10-22',
      refills: 3,
      isBlockchainVerified: false,
      isFHIRCompliant: true,
      pharmacy: {
        id: 'ph003',
        name: 'Rite Aid',
        address: '789 Pine Rd, City, State 12345'
      },
      fulfillmentStatus: 'processing'
    },
    {
      id: 'RX004',
      patientId: 'p004',
      patientName: 'David Thompson',
      medication: 'Amlodipine',
      dosage: '5mg',
      frequency: 'once_daily',
      duration: '30 days',
      instructions: 'Take at the same time each day. May cause ankle swelling.',
      status: 'active',
      priority: 'urgent',
      createdDate: '2025-09-21',
      expiryDate: '2025-10-21',
      refills: 1,
      isBlockchainVerified: true,
      isFHIRCompliant: true,
      pharmacy: {
        id: 'ph001',
        name: 'CVS Pharmacy',
        address: '123 Main St, City, State 12345'
      },
      fulfillmentStatus: 'ready'
    },
    {
      id: 'RX005',
      patientId: 'p001',
      patientName: 'Sarah Johnson',
      medication: 'Omeprazole',
      dosage: '20mg',
      frequency: 'once_daily',
      duration: '14 days',
      instructions: 'Take before breakfast. Complete full course.',
      status: 'cancelled',
      priority: 'normal',
      createdDate: '2025-09-18',
      expiryDate: '2025-10-18',
      refills: 0,
      isBlockchainVerified: true,
      isFHIRCompliant: true,
      pharmacy: null,
      fulfillmentStatus: null
    },
    {
      id: 'RX006',
      patientId: 'p002',
      patientName: 'Michael Chen',
      medication: 'Fluticasone/Salmeterol',
      dosage: '25/250mcg',
      frequency: 'twice_daily',
      duration: '30 days',
      instructions: 'Rinse mouth after use. Use spacer device.',
      status: 'expired',
      priority: 'normal',
      createdDate: '2025-08-15',
      expiryDate: '2025-09-15',
      refills: 2,
      isBlockchainVerified: true,
      isFHIRCompliant: true,
      pharmacy: {
        id: 'ph004',
        name: 'Local Pharmacy',
        address: '321 Elm St, City, State 12345'
      },
      fulfillmentStatus: 'completed'
    }
  ];

  const tabs = [
    { id: 'prescriptions', name: 'Prescriptions', icon: 'FileText' },
    { id: 'analytics', name: 'Analytics', icon: 'BarChart3' },
    { id: 'templates', name: 'Templates', icon: 'FileTemplate' }
  ];

  const sortOptions = [
    { value: 'createdDate', label: 'Created Date' },
    { value: 'patientName', label: 'Patient Name' },
    { value: 'medication', label: 'Medication' },
    { value: 'status', label: 'Status' },
    { value: 'priority', label: 'Priority' }
  ];

  useEffect(() => {
    setPrescriptions(mockPrescriptions);
    setFilteredPrescriptions(mockPrescriptions);
  }, []);

  useEffect(() => {
    let filtered = [...prescriptions];

    // Apply filters
    if (filters?.search) {
      filtered = filtered?.filter(prescription =>
        prescription?.patientName?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        prescription?.medication?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        prescription?.id?.toLowerCase()?.includes(filters?.search?.toLowerCase())
      );
    }

    if (filters?.status) {
      filtered = filtered?.filter(prescription => prescription?.status === filters?.status);
    }

    if (filters?.priority) {
      filtered = filtered?.filter(prescription => prescription?.priority === filters?.priority);
    }

    if (filters?.blockchainVerified) {
      filtered = filtered?.filter(prescription => prescription?.isBlockchainVerified);
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];

      if (sortBy === 'createdDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredPrescriptions(filtered);
  }, [prescriptions, filters, sortBy, sortOrder]);

  const handleCreatePrescription = () => {
    setEditingPrescription(null);
    setIsFormOpen(true);
  };

  const handleEditPrescription = (prescriptionId) => {
    const prescription = prescriptions?.find(p => p?.id === prescriptionId);
    setEditingPrescription(prescription);
    setIsFormOpen(true);
  };

  const handleViewPrescription = (prescriptionId) => {
    console.log('View prescription:', prescriptionId);
    // Implement view functionality
  };

  const handleRenewPrescription = (prescriptionId) => {
    console.log('Renew prescription:', prescriptionId);
    // Implement renewal functionality
  };

  const handleCancelPrescription = (prescriptionId) => {
    setPrescriptions(prev =>
      prev?.map(p =>
        p?.id === prescriptionId
          ? { ...p, status: 'cancelled' }
          : p
      )
    );
  };

  const handleFormSubmit = (formData) => {
    if (editingPrescription) {
      // Update existing prescription
      setPrescriptions(prev =>
        prev?.map(p =>
          p?.id === editingPrescription?.id
            ? { ...p, ...formData }
            : p
        )
      );
    } else {
      // Create new prescription
      const newPrescription = {
        id: `RX${String(prescriptions?.length + 1)?.padStart(3, '0')}`,
        ...formData,
        patientName: 'New Patient', // This would come from patient selection
        status: 'pending',
        createdDate: new Date()?.toISOString()?.split('T')?.[0],
        expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
        isBlockchainVerified: false,
        isFHIRCompliant: true,
        pharmacy: null,
        fulfillmentStatus: null
      };
      setPrescriptions(prev => [newPrescription, ...prev]);
    }
  };

  const handleUseTemplate = (templateData) => {
    setEditingPrescription(null);
    setIsFormOpen(true);
    // The form would be pre-filled with template data
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-semibold text-foreground">Prescription Management</h1>
              <p className="text-muted-foreground mt-2">
                Create, manage, and track e-prescriptions with FHIR compliance and blockchain verification
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="outline" iconName="Download">
                Export Data
              </Button>
              <Button iconName="Plus" onClick={handleCreatePrescription}>
                New Prescription
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-border mb-6">
            <nav className="flex space-x-8">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-clinical ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.name}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'prescriptions' && (
            <div className="space-y-6">
              {/* Filters */}
              <PrescriptionFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
              />

              {/* Sort and View Options */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-muted-foreground">
                    {filteredPrescriptions?.length} prescriptions
                  </span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-muted-foreground">Sort by:</span>
                    <select
                      value={sortBy}
                      onChange={(e) => handleSort(e?.target?.value)}
                      className="text-sm border border-border rounded-md px-2 py-1 bg-background text-foreground"
                    >
                      {sortOptions?.map((option) => (
                        <option key={option?.value} value={option?.value}>
                          {option?.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                      className="p-1 hover:bg-muted rounded transition-clinical"
                    >
                      <Icon 
                        name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
                        size={16} 
                        className="text-muted-foreground" 
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Prescriptions List */}
              {filteredPrescriptions?.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredPrescriptions?.map((prescription) => (
                    <PrescriptionCard
                      key={prescription?.id}
                      prescription={prescription}
                      onView={handleViewPrescription}
                      onEdit={handleEditPrescription}
                      onRenew={handleRenewPrescription}
                      onCancel={handleCancelPrescription}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No prescriptions found</h3>
                  <p className="text-muted-foreground mb-4">
                    {Object.keys(filters)?.length > 0
                      ? 'No prescriptions match your current filters.' :'Get started by creating your first prescription.'
                    }
                  </p>
                  <Button iconName="Plus" onClick={handleCreatePrescription}>
                    Create Prescription
                  </Button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'analytics' && <PrescriptionAnalytics />}

          {activeTab === 'templates' && (
            <PrescriptionTemplates onUseTemplate={handleUseTemplate} />
          )}
        </div>
      </div>
      {/* Prescription Form Modal */}
      <PrescriptionForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleFormSubmit}
        editingPrescription={editingPrescription}
      />
    </div>
  );
};

export default PrescriptionManagement;